import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Target,
  Heart,
  Users,
  Award,
  TrendingUp,
  Globe,
  Headphones,
} from 'lucide-react';

const AboutPage = () => {
  const { t } = useTranslation();

  const values = [
    { icon: Target, title: t('aboutPage.v1Title'), description: t('aboutPage.v1Desc') },
    { icon: Heart,  title: t('aboutPage.v2Title'), description: t('aboutPage.v2Desc') },
    { icon: Users,  title: t('aboutPage.v3Title'), description: t('aboutPage.v3Desc') },
    { icon: Award,  title: t('aboutPage.v4Title'), description: t('aboutPage.v4Desc') },
  ];

  const stats = [
    { value: '98%',  label: t('aboutPage.stat3Label'), icon: Heart,      color: 'bg-rose-100',   iconColor: 'text-rose-500' },
    { value: '24/7', label: t('aboutPage.stat4Label'), icon: Headphones, color: 'bg-primary-100', iconColor: 'text-primary-600' },
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Our Story & Mission | mwbTechs</title>
        <meta
          name="description"
          content="Learn about mwbTechs - our mission, values, team, and journey to becoming a leader in SaaS solutions."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                {t('aboutPage.heroTitle1')} <span className="gradient-text">{t('aboutPage.heroTitle2')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('aboutPage.heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/contact" className="btn btn-primary text-lg">
                  {t('aboutPage.getInTouch')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link to="/signup" className="btn btn-outline text-lg">
                  {t('aboutPage.startFreeTrial')}
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="section">
          <div className="container-custom">
            <div className="grid sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className="flex flex-col items-center text-center bg-white rounded-2xl border border-gray-100 shadow-md px-8 py-10"
                  >
                    <div className={`w-16 h-16 ${stat.color} rounded-2xl flex items-center justify-center mb-5`}>
                      <Icon className={`w-8 h-8 ${stat.iconColor}`} />
                    </div>
                    <div className="text-6xl font-extrabold text-gray-900 mb-2">{stat.value}</div>
                    <div className="text-gray-500 font-medium">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('aboutPage.storyTitle')}</h2>
                <div className="space-y-4 text-gray-600">
                  <p>{t('aboutPage.storyP1')}</p>
                  <p>{t('aboutPage.storyP2')}</p>
                  <p>{t('aboutPage.storyP3')}</p>
                  <p>{t('aboutPage.storyP4')}</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <Globe className="w-8 h-8 text-primary-600 mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">25+</div>
                    <div className="text-sm text-gray-600">{t('aboutPage.countriesServed')}</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <Award className="w-8 h-8 text-primary-600 mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">15+</div>
                    <div className="text-sm text-gray-600">{t('aboutPage.industryAwards')}</div>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <Users className="w-8 h-8 text-primary-600 mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">150+</div>
                    <div className="text-sm text-gray-600">{t('aboutPage.teamMembers')}</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <TrendingUp className="w-8 h-8 text-primary-600 mb-3" />
                    <div className="text-2xl font-bold text-gray-900 mb-1">200%</div>
                    <div className="text-sm text-gray-600">{t('aboutPage.yoyGrowth')}</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('aboutPage.valuesTitle')}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('aboutPage.valuesSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all text-center"
                  >
                    <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Team section hidden */}

        {/* Timeline section hidden */}

        {/* CTA */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">{t('aboutPage.ctaTitle')}</h2>
            <p className="text-xl text-blue-100 mb-8">
              {t('aboutPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/signup" className="btn btn-white text-lg">
                {t('aboutPage.startFreeTrial')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn btn-outline text-white border-white hover:bg-white hover:text-primary-600 text-lg">
                {t('aboutPage.contactUs')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
